import { customErrorMessagesCommon } from "../Validation";
import Validator from "validatorjs";
import { IVehicle, IDriver } from "../../classes/Domain";
import { appValidator } from "../Validation";
import { QUOTE_STEPS, QUOTE_QUESTIONS } from "../../AppConstants";
import { getStateQuoteConfig } from "../../config/StateQuoteConfig";

const isEmpty = (str) => (!str?.length);

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
    return value !== null && value !== undefined;
}

const VALIDATION = {
    VEHICLE_REQUIRED: "Vehicle is required",
    VEHICLE_MAKE_REQUIRED: "Vehicle make is required",
    VEHICLE_MODEL_REQUIRED: "Vehicle model is required",
    VEHICLE_BODY_REQUIRED: "Vehicle body is required",
    VEHICLE_COVERAGES_REQUIRED: "Vehicle coverages is required",
    VEHICLE_USE_REQUIRED: "Vehicle use is required",
    VEHICLE_OWNERSHIP_REQUIRED: "Please answer if the vehicle is leased",
    VEHICLE_MILEAGE_REQUIRED: "Annual mileage is required",
    VEHICLE_GARAGING_ZIP_REQUIRED: "Garaging zip is required",
    DRIVER_FIRST_NAME_REQUIRED: "Driver first name is required",
    DRIVER_LAST_NAME_REQUIRED: "Driver last name is required",
    DRIVER_BIRTH_DATE_REQUIRED: "Valid date of birth is required (named insured 18+, other drivers 16–99)",
    DRIVER_LICENSE_STATE_REQUIRED: "License state/country is required",
    DRIVER_AGE_REQUIRED: "Driver age is required",
    DRIVER_MARITAL_STATUS_REQUIRED: "Driver marital status is required",
    DRIVER_LICENSE_STATUS_REQUIRED: "Driver license status is required",
    DRIVER_LICENSE_STATUS_EXPIRED_CA: "Expired Licenses are not accepted for this program",
    DRIVER_GENDER_REQUIRED: "Driver gender is required",
    DRIVER_ACCIDENTS_VIOLATIONS_REQUIRED: "Violations info is required",
    INCIDENT_VIOLATION_REQUIRED: "Violation is required"
};

Validator.registerImplicit('vehicle_required', function (this: any, value, requirement, attribute) {
    const vehicles = this.validator.input.vehicles as Array<IVehicle>;

    if (vehicles == null || vehicles.length === 0 || vehicles.some(x => x == null)) {
        return false;
    }

    return true;
});

Validator.registerImplicit('vehicle_make_required', function (this: any, value, requirement, attribute) {
    const vehicles = this.validator.input.vehicles as Array<IVehicle>;

    if (vehicles.filter(notEmpty).some(x => isEmpty(x.make))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('vehicle_model_required', function (this: any, value, requirement, attribute) {
    const vehicles = this.validator.input.vehicles as Array<IVehicle>;

    if (vehicles.filter(notEmpty).some(x => isEmpty(x.model))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('vehicle_body_required', function (this: any, value, requirement, attribute) {
    const vehicles = this.validator.input.vehicles as Array<IVehicle>;

    if (vehicles.filter(notEmpty).some(x => isEmpty(x.body))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('vehicle_coverages_required', function (this: any, value, requirement, attribute) {
    const vehicles = this.validator.input.vehicles as Array<IVehicle>;

    if (vehicles.filter(notEmpty).some(x => !x.coverages)) {
        return false;
    }

    return true;
});

Validator.registerImplicit('vehicle_use_required', function (this: any, value, requirement, attribute) {
    const vehicles = this.validator.input.vehicles as Array<IVehicle>;

    if (vehicles.filter(notEmpty).some(x => isEmpty(x.vehicleUse))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('vehicle_ownership_required', function (this: any, value, requirement, attribute) {
    const vehicles = this.validator.input.vehicles as Array<IVehicle>;

    if (vehicles.filter(notEmpty).some(x => isEmpty(x.ownership))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('vehicle_mileage_required', function (this: any, value, requirement, attribute) {
    const stateCode = this.validator.input.stateCode as string;
    const cfg = getStateQuoteConfig(stateCode);
    if (!cfg?.requireAnnualMileage) {
        return true;
    }

    const vehicles = this.validator.input.vehicles as Array<IVehicle>;
    if (vehicles.filter(notEmpty).some(x => isEmpty(x.valueMiles))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('vehicle_garaging_zip_required', function (this: any, value, requirement, attribute) {
    const vehicles = this.validator.input.vehicles as Array<IVehicle>;

    if (vehicles.filter(notEmpty).some(x => isEmpty(x.garagingAddress?.zipCode))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('driver_firstName_required', function (this: any, value, requirement, attribute) {
    const drivers = this.validator.input.drivers as Array<IDriver>;

    if (drivers.filter(notEmpty).some(x => isEmpty(x.firstName))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('driver_lastName_required', function (this: any, value, requirement, attribute) {
    const drivers = this.validator.input.drivers as Array<IDriver>;

    if (drivers.filter(notEmpty).some(x => isEmpty(x.lastName))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('driver_birthDate_required', function (this: any, value, requirement, attribute) {
    const drivers = this.validator.input.drivers as Array<IDriver>;

    // mm/dd/yyyy; named insured (index 0) min 18 (was TX age dropdown); others min 16.
    if (drivers.filter(notEmpty).some((x, index) => {
        if (isEmpty(x.birthDate) || x.birthDate.length < 10) return true;
        const m = x.birthDate.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!m) return true;
        const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
        if (d.getFullYear() !== Number(m[3]) || d.getMonth() !== Number(m[1]) - 1 || d.getDate() !== Number(m[2])) {
            return true;
        }
        const today = new Date();
        let age = today.getFullYear() - d.getFullYear();
        const mdiff = today.getMonth() - d.getMonth();
        if (mdiff < 0 || (mdiff === 0 && today.getDate() < d.getDate())) age--;
        const minAge = index === 0 ? 18 : 16;
        return age < minAge || age > 99;
    })) {
        return false;
    }

    return true;
});

Validator.registerImplicit('driver_licenseState_required', function (this: any, value, requirement, attribute) {
    const drivers = this.validator.input.drivers as Array<IDriver>;

    if (drivers.filter(notEmpty).some(x => isEmpty(x.licenseState))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('driver_age_required', function (this: any, value, requirement, attribute) {
    // Age is derived from DOB; no separate age field on Qualitas quick quote.
    return true;
});

Validator.registerImplicit('driver_maritalStatus_required', function (this: any, value, requirement, attribute) {
    const drivers = this.validator.input.drivers as Array<IDriver>;

    if (drivers.filter(notEmpty).some(x => isEmpty(x.maritalStatus))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('driver_licenseStatus_required', function (this: any, value, requirement, attribute) {
    const drivers = this.validator.input.drivers as Array<IDriver>;

    if (drivers.filter(notEmpty).some(x => isEmpty(x.currentLicenseStatus))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('driver_licenseStatus_not_expired_ca', function (this: any, value, requirement, attribute) {
    const stateCode = this.validator.input.stateCode as string;
    if ((stateCode || "").toUpperCase() !== "CA") {
        return true;
    }

    const drivers = this.validator.input.drivers as Array<IDriver>;
    if (drivers.filter(notEmpty).some(x => x.currentLicenseStatus === QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.EXPIRED)) {
        return false;
    }

    return true;
});

Validator.registerImplicit('driver_gender_required', function (this: any, value, requirement, attribute) {
    const stateCode = this.validator.input.stateCode as string;
    const cfg = getStateQuoteConfig(stateCode);
    if (!cfg?.requireDriverAgeGender) {
        return true;
    }

    const drivers = this.validator.input.drivers as Array<IDriver>;

    if (drivers.filter(notEmpty).some(x => isEmpty(x.gender))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('driver_accidentsViolations_required', function (this: any, value, requirement, attribute) {
    const drivers = this.validator.input.drivers as Array<IDriver>;

    if (drivers.filter(notEmpty).some(x => isEmpty(x.accidentsViolations))) {
        return false;
    }

    return true;
});

Validator.registerImplicit('driver_viol_vialation_required', function (this: any, value, requirement, attribute) {
    const drivers = this.validator.input.drivers as Array<IDriver>;

    if (drivers.filter(notEmpty).some(x =>
        x.accidentsViolations === QUOTE_QUESTIONS.YES_NO.YES
        && (x.incidents == null || x.incidents.length === 0 || x.incidents.some(i => !i || isEmpty(i.violationCode)))
    )) {
        return false;
    }

    return true;
});

const vehicleRules = {
    year: 'vehicle_required',
    make: 'vehicle_make_required',
    model: 'vehicle_model_required',
    body: 'vehicle_body_required',
    coverages: 'vehicle_coverages_required',
    vehicleUse: 'vehicle_use_required',
    ownership: 'vehicle_ownership_required',
    valueMiles: 'vehicle_mileage_required',
    'garagingAddress.zipCode': 'vehicle_garaging_zip_required'
};

const driverRules = {
    firstName: 'driver_firstName_required',
    lastName: 'driver_lastName_required',
    birthDate: 'driver_birthDate_required',
    licenseState: 'driver_licenseState_required',
    age: 'driver_age_required',
    gender: 'driver_gender_required',
    maritalStatus: 'driver_maritalStatus_required',
    currentLicenseStatus: ['driver_licenseStatus_required', 'driver_licenseStatus_not_expired_ca'],
    accidentsViolations: 'driver_accidentsViolations_required'
};

const rules = {
    vehicle: vehicleRules,
    driver: driverRules,
    incidents: {
        violationCode: 'driver_viol_vialation_required'
    },
    contactInfo: {
        phone: ['required', 'valid_phone'],
        email: ['email']
    }
};

const rules_vehicle = {
    vehicle: vehicleRules
};

const rules_driver = {
    driver: driverRules,
    incidents: {
        violationCode: 'driver_viol_vialation_required'
    },
    contactInfo: {
        phone: ['required', 'valid_phone']
    }
};

const rules_discount = {
    vehicle: vehicleRules,
    driver: driverRules,
    incidents: {
        violationCode: 'driver_viol_vialation_required'
    },
    contactInfo: {
        phone: ['required', 'valid_phone']
    }
};

const rulesNoOwners = {
    driver: driverRules,
    incidents: {
        violationCode: 'driver_viol_vialation_required'
    },
    contactInfo: {
        phone: ['required', 'valid_phone']
    }
};

const customErrorMessages = {
    "vehicle_required": VALIDATION.VEHICLE_REQUIRED,
    "vehicle_make_required": VALIDATION.VEHICLE_MAKE_REQUIRED,
    "vehicle_model_required": VALIDATION.VEHICLE_MODEL_REQUIRED,
    "vehicle_body_required": VALIDATION.VEHICLE_BODY_REQUIRED,
    "vehicle_coverages_required": VALIDATION.VEHICLE_COVERAGES_REQUIRED,
    "vehicle_use_required": VALIDATION.VEHICLE_USE_REQUIRED,
    "vehicle_ownership_required": VALIDATION.VEHICLE_OWNERSHIP_REQUIRED,
    "vehicle_mileage_required": VALIDATION.VEHICLE_MILEAGE_REQUIRED,
    "vehicle_garaging_zip_required": VALIDATION.VEHICLE_GARAGING_ZIP_REQUIRED,
    "driver_firstName_required": VALIDATION.DRIVER_FIRST_NAME_REQUIRED,
    "driver_lastName_required": VALIDATION.DRIVER_LAST_NAME_REQUIRED,
    "driver_birthDate_required": VALIDATION.DRIVER_BIRTH_DATE_REQUIRED,
    "driver_licenseState_required": VALIDATION.DRIVER_LICENSE_STATE_REQUIRED,
    "driver_age_required": VALIDATION.DRIVER_AGE_REQUIRED,
    "driver_maritalStatus_required": VALIDATION.DRIVER_MARITAL_STATUS_REQUIRED,
    "driver_licenseStatus_required": VALIDATION.DRIVER_LICENSE_STATUS_REQUIRED,
    "driver_licenseStatus_not_expired_ca": VALIDATION.DRIVER_LICENSE_STATUS_EXPIRED_CA,
    "driver_gender_required": VALIDATION.DRIVER_GENDER_REQUIRED,
    "driver_accidentsViolations_required": VALIDATION.DRIVER_ACCIDENTS_VIOLATIONS_REQUIRED,
    "driver_viol_vialation_required": VALIDATION.INCIDENT_VIOLATION_REQUIRED
};

export const allFieldsValidation = (data) => {

    let _rules = {};

    if (data.quickStep === QUOTE_STEPS.QUICK_VEHICLE) {
        _rules = rules_vehicle;
    }
    else if (data.quickStep === QUOTE_STEPS.QUICK_DRIVER) {
        _rules = rules_driver;
    }
    else if (data.quickStep === QUOTE_STEPS.QUICK_DISCOUNT) {
        _rules = data.isNonOwners ? rulesNoOwners : rules_discount;
    }
    else {
        _rules = data.isNonOwners ? rulesNoOwners : rules;
    }

    return appValidator.validate(data, _rules, { ...customErrorMessagesCommon, ...customErrorMessages });
};
